import { useTranslate } from "@refinedev/core";
import { Button, Checkbox, Flex, Form, Typography } from "antd";

import { useMediaAssetUpload } from "hooks/useMediaAssetUpload";
import { useOrganization } from "hooks/useOrganization";
import { FaceProfileCreationRequest } from "../types";
import { CustomFormItemProps } from "types";
import { FaceProfilePreview } from "./FaceProfilePreview";
import { useAntTheme } from "hooks/useAntTheme";
import { useState } from "react";
import { useForm } from "antd/es/form/Form";
import { useAnalytics } from "hooks/useAnalytics";
import { AnalyticsEvents } from "types/analytics";
import { BigInput } from "components/BigInput/BigInput/";
import { StyledDragger } from "components/uploader/UploadFiles";
import { FaceProfileCreationResponse } from "./ContactWithPresenter/types";
import { radius } from "@libertify/ui";
const ACCEPTED_MIME_TYPES =
  ".png, .jpeg, .jpg, image/jpeg, image/jpg, image/png";

export const FaceProfileCreateForm: React.FC<
  CustomFormItemProps<string> & {
    name?: string;
    onChange?: (faceId: string) => void;
    onSuccess?: (face: FaceProfileCreationResponse) => void;
  }
> = ({ name = "", value, onChange, onSuccess }) => {
  const t = useTranslate();
  const { theme } = useAntTheme();
  const { trackEvent } = useAnalytics();
  const { colorPrimary } = useAntTheme();
  const { organization } = useOrganization({});
  const [loading, setLoading] = useState(false);
  const [canCloneFace, setCanCloneFace] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [hasName, setHasName] = useState(!!name);
  const { uploadFiles, customRequest } = useMediaAssetUpload({
    organization: organization?.id,
    assetType: "Picture",
    buildCreateAssetEndpoint(file: File) {
      return `/media/${organization?.id}/face_profiles`;
    },
    buildCreateAssetParams(file: File) {
      return {};
    },
    buildCreateAssetPayload(file: File): FaceProfileCreationRequest {
      return {
        name:
          form.getFieldValue("name") ??
          t("media.components.FaceProfileCreateForm.faceDefaultName"),
        category: "Custom",
        custom_image_filename: file.name,
      };
    },
    parseAsset(response: unknown) {
      return (response as any).asset;
    },
  });

  const handleFinish = async () => {
    setLoading(true);
    const responses = await uploadFiles<unknown, FaceProfileCreationResponse>();
    setLoading(false);
    // console.debug({ responses });
    const { face_profile_id } = responses[0] ?? {};
    onChange?.(face_profile_id);
    onSuccess?.(responses[0]);
    trackEvent(AnalyticsEvents.FaceCloneCreate, {
      face_profile_id: face_profile_id,
    });
  };

  const [form] = useForm();

  if (value) {
    return (
      <FaceProfilePreview
        style={{
          padding: 2,
          borderColor: colorPrimary,
        }}
        id={value}
      />
    );
  }

  return (
    <Flex vertical gap={24} align="center">
      <Flex align="center" vertical>
        <Typography.Text style={{ fontSize: 28, marginBottom: -5 }}>
          {t("media.components.FaceProfileCreateForm.cloneNewFace")}
        </Typography.Text>
        <Typography.Text>
          {t("media.components.FaceProfileCreateForm.uploadAPicture")}
        </Typography.Text>
      </Flex>
      <Form
        form={form}
        initialValues={{ name }}
        layout="vertical"
        onValuesChange={(changedValues) => {
          if ("name" in changedValues) {
            setHasName(!!changedValues.name?.trim());
          }
        }}
      >
        <Form.Item
          label={t("media.components.FaceProfileCreateForm.name")}
          name="name"
          rules={[
            {
              required: true,
              message: t("media.components.FaceProfileCreateForm.pleaseEnterA"),
            },
          ]}
        >
          <BigInput size="small" style={{ maxWidth: 400 }} />
        </Form.Item>
      </Form>
      <StyledDragger
        borderColor={theme.colorPrimary}
        maxCount={1}
        accept={ACCEPTED_MIME_TYPES}
        name="file"
        multiple={false}
        iconRender={(_file) => null}
        customRequest={(options) => {
          setCanCloneFace(true);
          customRequest(options);
        }}
      >
        <Flex align="center" justify="center" vertical style={{ padding: 20 }}>
          <Typography.Text>
            {t("media.components.FaceProfileCreateForm.clickOrDrag")}
          </Typography.Text>
          <Typography.Text type="secondary">
            {t("media.components.FaceProfileCreateForm.acceptedFormats")}
          </Typography.Text>
          {/* todo */}
          {/* {isUploading && <Progress percent={completed} size="small" />} */}
        </Flex>
      </StyledDragger>
      <Flex vertical gap={20} align="center">
        <Checkbox
          checked={consentChecked}
          onChange={(e) => setConsentChecked(e.target.checked)}
          style={{
            backgroundColor: "#f5f5f5",
            padding: "12px 16px",
            borderRadius: radius.box,
            width: "100%",
            maxWidth: "600px",
          }}
        >
          <Typography.Text>
            {t("media.components.FaceProfileCreateForm.consentText")}
          </Typography.Text>
        </Checkbox>

        <Button
          size="large"
          type="primary"
          shape="round"
          loading={loading}
          onClick={handleFinish}
          disabled={!hasName || !canCloneFace || !consentChecked}
        >
          {t("media.components.FaceProfileCreateForm.cloneFace")}
        </Button>
      </Flex>
    </Flex>
  );
};
